import { ComponentChildren, ComponentProps } from 'preact';
import { DisruptionAlert } from '../../../base/DisruptionAlert/DisruptionAlert';
import type { DisruptionLink } from '../../../../arcticapi/types';
import styles from './JourneyPlanItineraryStep.module.css';
import { CSSProperties } from 'preact';

interface MarkerProps {
  variant?: 'default' | 'line';
  hideCircle?: boolean;
  lineColor?: string;
  lineTopColor?: string;
  lineBottomColor?: string;
  circleColor?: string;
  circleBorderWidth?: string;
}

export interface JourneyPlanItineraryStepProps extends ComponentProps<'li'> {
  children?: ComponentChildren;
  details?: ComponentChildren;
  disruption?: DisruptionLink | null;
  hasContentDivider?: boolean;
  timeLabel?: string;
  timeDateTime?: string;
  marker?: MarkerProps;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

/*The vertical bar and circle marker for each step in the journey itinerary.*/
function Marker({
  variant = 'default',
  hideCircle = false,
}: Pick<MarkerProps, 'variant' | 'hideCircle'>) {
  const markerClassNames = [
    styles.marker,
    variant === 'default' ? styles.defaultMarker : '',
    hideCircle ? styles.hideMarkerCircle : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span aria-hidden="true" className={markerClassNames}>
      <span className={styles.markerBar} />
      <span className={styles.markerCircle} />
    </span>
  );
}

/**
 * Represents one step (not leg) in a journey itinerary.
 */
export function JourneyPlanItineraryStep({
  children,
  details,
  disruption,
  hasContentDivider = true,
  timeLabel,
  timeDateTime,
  marker,
  isFirstStep = false,
  isLastStep = false,
  className,
  style,
  ...props
}: JourneyPlanItineraryStepProps) {
  const stepClassNames = [
    styles.step,
    isFirstStep ? styles.firstStep : '',
    isLastStep ? styles.lastStep : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const styleOverrides =
    style &&
    typeof style === 'object' &&
    !Array.isArray(style) &&
    !('value' in style)
      ? (style as CSSProperties)
      : undefined;

  const contentClassNames = [
    styles.content,
    hasContentDivider ? '' : styles.contentNoDivider,
  ]
    .filter(Boolean)
    .join(' ');

  /* Set CSS variables for line colors and widths based on marker props. */
  const stepStyle: CSSProperties = {
    ...(styleOverrides ?? {}),
    '--journey-step-line-color': marker?.lineColor ?? 'var(--internal-divider)',
    '--journey-step-line-top-color':
      marker?.lineTopColor ?? marker?.lineColor ?? 'var(--internal-divider)',
    '--journey-step-line-bottom-color':
      marker?.lineBottomColor ?? marker?.lineColor ?? 'var(--internal-divider)',
    '--journey-step-marker-circle-color':
      marker?.circleColor ?? marker?.lineColor ?? 'var(--internal-divider)',
    '--journey-step-marker-circle-border-width':
      marker?.circleBorderWidth ?? 'var(--journey-step-line-width)',
  };

  return (
    <li {...props} className={stepClassNames} style={stepStyle}>
      {timeLabel ? (
        <time className={styles.time} dateTime={timeDateTime}>
          {timeLabel}
        </time>
      ) : (
        <span aria-hidden="true" className={styles.timePlaceholder} />
      )}
      <Marker variant={marker?.variant} hideCircle={marker?.hideCircle} />
      <div className={contentClassNames}>
        <div className={styles.actionRow}>
          <p className={styles.action}>{children}</p>
          <DisruptionAlert disruption={disruption} />
        </div>
        {details ? <div className={styles.details}>{details}</div> : null}
      </div>
    </li>
  );
}
